
import { getNodeIdAtPointInCanvas } from "@/lib/canvas/drag/element-drag";
import {
  getTreeNodePositionAndSize,
  getNodeElementWrapperStyles,
  getDomElementFromTreeNode
} from "@/lib/canvas/helpers";
import { findNode } from "@/lib/tree/tree";
import { documentStore, uiStore } from "@/store/store";
import { Component, Vue } from "vue-property-decorator";
import throttle from "lodash/throttle";
import { forceNextTick } from "@/util/force-next-tick";
import NodeOnboardingPaletteButtons from "./NodeOnboardingPaletteButtons.vue";
import NodeOnboardingAIRewriteButton from "./NodeOnboardingAIRewriteButton.vue";

@Component({
  components: {
    NodeOnboardingPaletteButtons,
    NodeOnboardingAIRewriteButton
  }
})
export default class NodeOnboardingHoverControls extends Vue {
  interval!: NodeJS.Timer;
  refreshRate = 500;
  shouldShowControls = true;

  unsubscribeFromStorePositionWrapper = () => {};

  throttledNextTickPositionWrapper = throttle(
    () => forceNextTick(() => this.positionWrapper()),
    100
  );

  beforeMount() {
    this.positionWrapper();

    this.unsubscribeFromStorePositionWrapper = this.$store.subscribe(
      this.throttledNextTickPositionWrapper
    );

    window.addEventListener("resize", this.throttledNextTickPositionWrapper);
  }

  mounted() {
    const canvasElement =
      window.Breakdance.canvas.document.getElementById("breakdance_canvas");

    if (!canvasElement) return;

    canvasElement.addEventListener(
      "mousemove",
      this.throttledShowHoverControlsOnMouseMoveCanvas
    );
    canvasElement.addEventListener(
      "mouseleave",
      this.disableMouseHoverOnMouseLeaveCanvas
    );

    this.interval = setInterval(
      this.throttledNextTickPositionWrapper,
      this.refreshRate
    );
  }

  beforeDestroy() {
    const canvasElement =
      window.Breakdance.canvas.document.getElementById("breakdance_canvas");

    this.unsubscribeFromStorePositionWrapper();
    window.removeEventListener("resize", this.throttledNextTickPositionWrapper);
    canvasElement?.removeEventListener(
      "mousemove",
      this.throttledShowHoverControlsOnMouseMoveCanvas
    );
    clearInterval(this.interval);
  }

  get wrapperClassList() {
    return {
      "node-hover-controls-show": true,
      "node-hover-controls--outline": this.isNotDragging
    };
  }

  get isNotDragging() {
    return !uiStore.isCanvasElementBeingDragged && !uiStore.isDragMixinDragging;
  }

  get element() {
    if (
      !uiStore.lastHoveredOnCanvasElementId ||
      uiStore.elementWithContentEditable
    )
      return false;

    const lastHoveredElement = findNode(
      documentStore.document.tree,
      uiStore.lastHoveredOnCanvasElementId
    );

    return lastHoveredElement || false;
  }

  setHoveredNode(x: number, y: number) {
    const hoveredNodeId = getNodeIdAtPointInCanvas(x, y);

    if (hoveredNodeId !== undefined) {
      const hoveredNode = findNode(documentStore.document.tree, hoveredNodeId);

      // onboarding should be section based
      if (
        hoveredNode &&
        hoveredNode.data.type === "EssentialElements\\Section"
      ) {
        uiStore.setLastHoveredOnCanvasElementId(hoveredNode.id);
      }
    }
  }

  disableMouseHoverOnMouseLeaveCanvas = () => {
    // without a timeout, the mousemove event will trigger after mouseleave
    // setting the hoveredElement again
    setTimeout(() => uiStore.setLastHoveredOnCanvasElementId(null), 110);
  };

  throttledShowHoverControlsOnMouseMoveCanvas = throttle(
    this.showHoverControlsOnMouseMoveCanvas,
    100
  );
  showHoverControlsOnMouseMoveCanvas(e: MouseEvent) {
    const { clientX: mouseX, clientY: mouseY } = e;

    this.setHoveredNode(mouseX, mouseY);

    if (this.element === false) return;

    const elementPositionAndSize = getTreeNodePositionAndSize(
      this.element,
      "absolute"
    );

    if (!elementPositionAndSize) return;

    const {
      x: elementX,
      y: elementY,
      width: elementWidth,
      height: elementHeight
    } = elementPositionAndSize;

    this.shouldShowControls =
      mouseX > elementX + elementWidth - 48 &&
      mouseX < elementX + elementWidth &&
      mouseY > elementY &&
      mouseY < elementY + elementHeight;
  }

  wrapperPositionStyle: { [key: string]: string } = {
    display: "none"
  };

  positionWrapper() {
    if (uiStore.elementWithContentEditable || !this.element) {
      this.wrapperPositionStyle = {
        display: "none"
      };

      return;
    }

    this.wrapperPositionStyle = getNodeElementWrapperStyles(
      getDomElementFromTreeNode(this.element)
    );
  }
}
